import { inject, Injectable, signal } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { formatEther } from "viem";

import { Config, readContracts } from "@wagmi/core";
import { stakeContract } from "../../shared/constants/wallet-connect.const";
import { environment } from "../../../environments/environment";
import { IStakeData } from "../../shared/interfaces/wallet.interface";
import { ReownService } from "../connection-services/reown.service";

@Injectable({
    providedIn: 'root',
})


export class StakingService {
    private wagmiConfig!: Config
    public refreshId!: NodeJS.Timeout;
    public walletAddress: string = "";
    public chainId: string = "";

    private readonly _stakeData = signal<IStakeData>({} as IStakeData);
    readonly stakeData = this._stakeData.asReadonly();

    private readonly reownService = inject(ReownService);

    constructor() {
        toObservable(this.reownService.connectionItems).subscribe((item) => {
            if (item.wagmiConfig) {
                this.wagmiConfig = item.wagmiConfig;
                if (item.walletAddressEVM) this.walletAddress = item.walletAddressEVM;
                this.prepareStakingData();
            }
        });
    }

    /** Updates the user's token wallet balance within stakeData — called by PresaleDataService after getBalance() */
    updateUserBal(balance: number): void {
        this._stakeData.update(current => ({ ...current, userBal: balance }));
    }

    async prepareStakingData() {
        const smartContract = {
            contracts: [
                // =========== Staking Contract ==============
                {
                    ...stakeContract,
                    functionName: 'poolStakers',
                    chainId: environment.eth.chainIdInt, // 0
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...stakeContract,
                    functionName: 'getRewards',
                    chainId: environment.eth.chainIdInt, // 1
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...stakeContract,
                    functionName: 'lockedTime',
                    chainId: environment.eth.chainIdInt, // 2
                    args: [],
                },
                {
                    ...stakeContract,
                    functionName: 'apy',
                    chainId: environment.eth.chainIdInt, // 3
                    args: [],
                },
                {
                    ...stakeContract,
                    functionName: 'tokensStaked',
                    chainId: environment.eth.chainIdInt, // 4
                    args: [],
                },
                {
                    ...stakeContract,
                    functionName: 'harvestLock',
                    chainId: environment.eth.chainIdInt, // 5
                    args: [],
                },
                {
                    ...stakeContract,
                    functionName: 'rewardTokensPerBlock',
                    chainId: environment.eth.chainIdInt, // 6
                    args: [],
                },
            ],
        } as const;

        const _stakeData: any = await readContracts(this.wagmiConfig, smartContract);
        const totalStaked = +formatEther(_stakeData[4].result || 0);
        this._stakeData.set({
            ...this._stakeData(),
            userStaked: +formatEther(_stakeData[0].result[0] || 0),
            unlockTime: Number(_stakeData[0].result[1]) + Number(_stakeData[2].result),
            userReward: +formatEther(_stakeData[1]?.result || 0),
            totalStaked,
            apy: +((+environment.apyCalcConst / totalStaked) * 100).toFixed(0),
            isClaimEnabled: !_stakeData[5].result || false,
            rewardPerRound: +formatEther(_stakeData[6].result || 0),
        });
        clearTimeout(this.refreshId);
        this.refreshId = setTimeout(() => {
            this.prepareStakingData();
        }, 5000);
    }

}
